import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ChatExampleListResponseDto,
  CreatePromptDraftDto,
  FewshotCoverageResponseDto,
  FewshotExampleDto,
  FewshotFlagsDto,
  FewshotListResponseDto,
  StudioExampleCreatedDto,
  StudioExampleDto,
  StudioOptionsResponseDto,
  StudioPlaygroundDto,
  StudioPlaygroundResponseDto,
  PromptContentDto,
  PromptDraftDto,
  PromptDraftListResponseDto,
  PromptSlotsResponseDto,
  StudioChatDto,
  StudioChatExampleDto,
  StudioChatReplyDto,
  UpdatePromptDraftDto,
} from '@lunary/api-client'
import { api } from '../../shared/api/client'

export function useStudioOptions() {
  return useQuery({
    queryKey: ['studio-options'],
    queryFn: () => api.get<StudioOptionsResponseDto>('/trainer/studio/options'),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function usePlayground() {
  return useMutation({
    mutationFn: (body: StudioPlaygroundDto) => api.post<StudioPlaygroundResponseDto>('/trainer/playground', body),
  })
}

function invalidateFewshot(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['fewshot'] })
  void qc.invalidateQueries({ queryKey: ['fewshot-coverage'] })
  void qc.invalidateQueries({ queryKey: ['trainer-coverage'] })
}

export function useSaveExample() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StudioExampleDto) => api.post<StudioExampleCreatedDto>('/trainer/examples', body),
    onSuccess: () => invalidateFewshot(qc),
  })
}

export function useFewshotCoverage() {
  return useQuery({
    queryKey: ['fewshot-coverage'],
    queryFn: () => api.get<FewshotCoverageResponseDto>('/trainer/fewshot/coverage'),
    retry: false,
  })
}

export function useFewshot(cardId: string) {
  return useQuery({
    queryKey: ['fewshot', cardId],
    queryFn: () => api.get<FewshotListResponseDto>('/trainer/fewshot', { query: { card_id: cardId, limit: 100 } }),
    enabled: cardId.length > 0,
    retry: false,
  })
}

export function useSetFewshotFlags() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, flags }: { id: string; flags: FewshotFlagsDto }) =>
      api.patch<FewshotExampleDto>(`/trainer/fewshot/${id}`, flags),
    onSuccess: () => invalidateFewshot(qc),
  })
}

export function useChatReply() {
  return useMutation({
    mutationFn: (body: StudioChatDto) => api.post<StudioChatReplyDto>('/trainer/chat', body),
  })
}

export function useSaveChatExample() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: StudioChatExampleDto) => api.post<StudioExampleCreatedDto>('/trainer/chat/examples', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['chat-examples'] }),
  })
}

export function useChatExamples() {
  return useQuery({
    queryKey: ['chat-examples'],
    queryFn: () => api.get<ChatExampleListResponseDto>('/trainer/chat/examples', { query: { limit: 30 } }),
    retry: false,
  })
}

export function usePromptSlots() {
  return useQuery({
    queryKey: ['prompt-slots'],
    queryFn: () => api.get<PromptSlotsResponseDto>('/trainer/prompts'),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

export function usePromptContent(scope: string, name: string, version: number) {
  return useQuery({
    queryKey: ['prompt-content', scope, name, version],
    queryFn: () => api.get<PromptContentDto>('/trainer/prompts/content', { query: { scope, name, version } }),
    enabled: version > 0,
    staleTime: Infinity,
    retry: false,
  })
}

export function usePromptDrafts() {
  return useQuery({
    queryKey: ['prompt-drafts'],
    queryFn: () => api.get<PromptDraftListResponseDto>('/trainer/prompt-drafts'),
    retry: false,
  })
}

export function useCreatePromptDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreatePromptDraftDto) => api.post<PromptDraftDto>('/trainer/prompt-drafts', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prompt-drafts'] }),
  })
}

export function useUpdatePromptDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePromptDraftDto }) =>
      api.patch<PromptDraftDto>(`/trainer/prompt-drafts/${id}`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prompt-drafts'] }),
  })
}

export function useDeletePromptDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/trainer/prompt-drafts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['prompt-drafts'] }),
  })
}
